import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { videos } from '@/mocks/videos';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(
    () =>
      articles
        .filter((a) => a.slug !== slug && a.categorySlug === article?.categorySlug)
        .slice(0, 3),
    [article, slug]
  );

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <main>
          <Navbar />
          <div className="pt-16 min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-file-search-line text-3xl"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">
                文章未找到
              </h1>
              <p className="text-sm text-foreground-500 mb-6">
                抱歉，您要查看的文章不存在或已下架
              </p>
              <Link
                to="/"
                className="inline-block px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          </div>
          <Footer />
        </main>
      </>
    );
  }

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '鲍鱼tv',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.baoyutv.com/article/${article.slug}`,
    },
  };

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleLd)}</script>

      <main>
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <article className="w-full py-10 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 text-xs bg-accent-100 text-accent-700 rounded-full font-medium">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}阅读
                </span>
              </div>
            </div>
          </article>

          {/* Cover Image */}
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 -mt-4">
            <div className="aspect-video rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Article Content */}
          <section className="w-full py-10 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="prose-custom text-foreground-800 leading-relaxed">
                {paragraphs.map((para, index) => {
                  if (para.startsWith('## ')) {
                    return (
                      <h2
                        key={index}
                        className="text-lg font-heading font-bold text-foreground-900 mt-8 mb-3"
                      >
                        {para.replace('## ', '')}
                      </h2>
                    );
                  }
                  if (para.startsWith('### ')) {
                    return (
                      <h3
                        key={index}
                        className="text-base font-heading font-bold text-foreground-900 mt-6 mb-2"
                      >
                        {para.replace('### ', '')}
                      </h3>
                    );
                  }
                  return (
                    <p key={index} className="text-sm text-foreground-700 mb-4 leading-loose">
                      {para}
                    </p>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full py-10 bg-background-100">
              <div className="max-w-6xl mx-auto px-4 md:px-6">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>

        <Footer />
      </main>
    </>
  );
}